import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Upload, Eye, EyeOff, ArrowLeft, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({ onBack, onLogout }) => {
  const [sliders, setSliders] = useState([]);
  const [editingSlider, setEditingSlider] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    title_tr: '',
    description: '',
    description_tr: '',
    image_url: '',
    link: '',
    order: 0,
    active: true
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const response = await axios.get(`${API}/sliders?active_only=false`);
      setSliders(response.data);
    } catch (error) {
      console.error('Error fetching sliders:', error);
      toast({
        title: "Error",
        description: "Failed to load sliders",
        variant: "destructive"
      });
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      title: '',
      title_tr: '',
      description: '',
      description_tr: '',
      image_url: '',
      link: '',
      order: sliders.length,
      active: true
    });
  };

  const handleEdit = (slider) => {
    setEditingSlider(slider.id);
    setFormData({
      title: slider.title,
      title_tr: slider.title_tr || '',
      description: slider.description,
      description_tr: slider.description_tr || '',
      image_url: slider.image_url,
      link: slider.link || '',
      order: slider.order,
      active: slider.active
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingSlider(null);
    setFormData({
      title: '',
      title_tr: '',
      description: '',
      description_tr: '',
      image_url: '',
      link: '',
      order: 0,
      active: true
    });
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        await axios.post(`${API}/sliders`, formData);
        toast({
          title: "Success",
          description: "Slider created successfully"
        });
      } else if (editingSlider) {
        await axios.put(`${API}/sliders/${editingSlider}`, formData);
        toast({
          title: "Success",
          description: "Slider updated successfully"
        });
      }
      handleCancel();
      fetchSliders();
    } catch (error) {
      console.error('Error saving slider:', error);
      toast({
        title: "Error",
        description: "Failed to save slider",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slider?')) {
      return;
    }

    try {
      await axios.delete(`${API}/sliders/${id}`);
      toast({
        title: "Success",
        description: "Slider deleted successfully"
      });
      fetchSliders();
    } catch (error) {
      console.error('Error deleting slider:', error);
      toast({
        title: "Error",
        description: "Failed to delete slider",
        variant: "destructive"
      });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/sliders/upload-image`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ ...formData, image_url: response.data.image_url });
      toast({
        title: "Success",
        description: "Image uploaded successfully"
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const toggleActive = async (slider) => {
    try {
      await axios.put(`${API}/sliders/${slider.id}`, {
        active: !slider.active
      });
      fetchSliders();
      toast({
        title: "Success",
        description: `Slider ${!slider.active ? 'activated' : 'deactivated'}`
      });
    } catch (error) {
      console.error('Error toggling slider:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Viewer
            </Button>
            <h1 className="text-3xl font-bold">Slider Admin Panel</h1>
          </div>
          <div className="flex gap-2">
            {!isCreating && !editingSlider && (
              <Button onClick={handleCreate} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                New Slider
              </Button>
            )}
            <Button onClick={onLogout} variant="outline" className="text-red-600 border-red-600 hover:bg-red-50">
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış Yap
            </Button>
          </div>
        </div>

        {/* Edit/Create Form */}
        {(isCreating || editingSlider) && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">
              {isCreating ? 'Create New Slider' : 'Edit Slider'}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title (English)</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full p-2 border rounded"
                  placeholder="Enter title in English"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Title (Turkish)</label>
                <input
                  type="text"
                  value={formData.title_tr}
                  onChange={(e) => setFormData({ ...formData, title_tr: e.target.value })}
                  className="w-full p-2 border rounded"
                  placeholder="Türkçe başlık girin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description (English)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-2 border rounded"
                  rows={3}
                  placeholder="Enter description in English"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description (Turkish)</label>
                <textarea
                  value={formData.description_tr}
                  onChange={(e) => setFormData({ ...formData, description_tr: e.target.value })}
                  className="w-full p-2 border rounded"
                  rows={3}
                  placeholder="Türkçe açıklama girin"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Image URL or Upload</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="flex-1 p-2 border rounded"
                    placeholder="Enter image URL or upload below"
                  />
                  <label className="cursor-pointer">
                    <Button disabled={uploadingImage} className="pointer-events-none">
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingImage ? 'Uploading...' : 'Upload'}
                    </Button>
                    <input
                      type="file"
                      accept="image/*,.gif"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                </div>
                {formData.image_url && (
                  <img src={formData.image_url} alt="Preview" className="mt-2 h-24 rounded" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Link (Optional)</label>
                <input
                  type="text"
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  className="w-full p-2 border rounded"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label className="text-sm font-medium">Active</label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button onClick={handleCancel} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Sliders List */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sliders.map((slider) => (
                <tr key={slider.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{slider.order}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="font-medium">{slider.title}</div>
                    <div className="text-gray-500 text-xs">{slider.title_tr}</div>
                  </td>
                  <td className="px-6 py-4">
                    <img src={slider.image_url} alt="Slider" className="h-12 rounded" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      slider.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {slider.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleActive(slider)}
                      >
                        {slider.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(slider)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(slider.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;